"use client";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ospThemes } from "@ospweb/sdk-ui";
import { cn, getAssetsUrl, pubsub } from "utils";
import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import EdgenDrawerCallable from "../EdgenDrawerCallable";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { ChatStore, rootStore } from "@/src/store";
import { useOpenChatModal } from "@/src/hooks/useOpenChatModal";
import { useRecommendQuestion } from "@/src/hooks";
import { SCOPE_MAP } from "@/src/const";
import { getRandomElements } from "@/src/utils";

interface ChatEntryProps {
  className?: string;
  /** 是否旋转图标（用于作为 Drawer 把手时） */
  rotateIcon?: boolean;
}

/**
 * ChatEntry 聊天入口组件
 * 可作为页面入口按钮，也可作为 Drawer 的小把手
 * 会自动根据 drawer 的打开/关闭状态旋转图标和调整位置
 */
export const ChatEntry = (props?: ChatEntryProps) => {
  const { className = "" } = props || {};
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleOpenChatModal = useOpenChatModal();
  const { user } = rootStore;
  const { ownerInfo } = useSnapshot(user);

  const scopeKey = SCOPE_MAP.AGENT_SEARCH;
  const requestParams = {
    [`scopes[${scopeKey}]`]: 20,
    related: ownerInfo?.id as any,
  };
  const { questions } = useRecommendQuestion(requestParams, {
    key: scopeKey,
  });

  const handleEntryClick = async () => {
    if (isDrawerOpen) {
      // Drawer 已打开，点击关闭
      // 先触发关闭动画
      await EdgenDrawerCallable.update({ isOpen: false });
      setIsDrawerOpen(false);

      // 等待关闭动画完成后再销毁组件（300ms 与 createCallable 的 timeout 一致）
      setTimeout(() => {
        if (!ChatStore.loading) {
          EdgenDrawerCallable.end(false);
          ChatStore.hasChatModal = false;
        }
      }, 300);
    } else {
      // Drawer 未打开，点击打开 ChatModal
      setIsDrawerOpen(true);
      if (ChatStore.hasChatModal) {
        await EdgenDrawerCallable.update({ isOpen: true });
        return;
      }
      handleOpenChatModal({ questions: getRandomElements(questions || [], 6) });
    }
  };

  useEffect(() => {
    const unsubscribe = pubsub.on(
      pubSubKeyMap.CHAT_DRAWER_STATE_CHANGE,
      (isOpen: boolean) => {
        setIsDrawerOpen(isOpen);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div>
      <div
        data-openreplay-label={isDrawerOpen ? "关闭聊天" : "打开聊天"}
        className={cn(
          "flex h-[86px] w-[15px] fixed right-0 top-1/2 z-[102] ease-in-out duration-350 -translate-y-1/2 items-center bg-cover bg-center cursor-pointer transition-all",
          className,
          {
            "right-[816px]": isDrawerOpen,
          },
        )}
        style={{
          backgroundImage: `url(${getAssetsUrl("/assets/images/chat_btn_bg_v2.webp")})`,
        }}
        onClick={handleEntryClick}
        role="button"
        aria-label={isDrawerOpen ? "关闭聊天" : "打开聊天"}
      >
        <OspIcons
          name="first-page"
          size={16}
          color={ospThemes.colors.content_brand_primary}
          className={cn("transition-transform duration-300", {
            "rotate-180": isDrawerOpen,
          })}
        />
      </div>
    </div>
  );
};
